import { useCallback, useMemo } from 'react';
import { Navigate, useNavigate } from '@tanstack/react-router';

import { useGetPageProducts } from '@/api/hooks/queries/use-get-product-catalog-query';
import bloodPressureMonitorImage from '@/assets/products/blood_pressure_monitor.png';
import Hr from '@/components/common/Hr.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Label } from '@/components/ui/label.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { useProductSelectionStep } from '@/hooks/use-product-selection-step';
import { useSessionSave } from '@/hooks/use-session-save';
import {
  addProductIds,
  removeProductIds,
  useSessionStore,
} from '@/hooks/use-session-store/use-session-store';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';
import { BLOOD_PRESSURE_MONITOR_NAME, PRODUCT_STEPS } from '@/lib/products.ts';
import { getActionablePlanFamilyCarrierTypes } from '@/types/insurance-builder.type.ts';
import { CATEGORY_TYPE } from '@/types/product.type.ts';
import ProductStepper from '@/views/layouts/ProductStepper.tsx';
import ProductRadioGroup from '@/views/products/components/ProductRadioGroup.tsx';
import { ProductSelectionSubmitFeedback } from '@/views/products/components/ProductSelectionSubmitFeedback.tsx';
import SkipButton from '@/views/products/components/SkipButton.tsx';

export default function SelectDiabetes() {
  const { saveSession } = useSessionSave();
  const { insuranceBuilder, selectedProductIds } = useSessionStore();

  const { products, isSuccess } = useGetPageProducts({
    categoryType: CATEGORY_TYPE.DIABETES,
    patientCarrierTypes: getActionablePlanFamilyCarrierTypes(insuranceBuilder),
  });

  const normalProducts = useMemo(
    // blood pressure monitor gets special treatment in display and selection
    // but is still a product in the cart at the end of the day
    () => products.filter((p) => p.name !== BLOOD_PRESSURE_MONITOR_NAME),
    [products]
  );

  const diabetesId = useMemo(() => {
    return normalProducts.find((p) => selectedProductIds.includes(p.uuid))?.uuid ?? '';
  }, [normalProducts, selectedProductIds]);

  const updateDiabetes = useCallback(
    (productId: string) => {
      // Handle deselection - remove all diabetes products
      if (!productId) {
        removeProductIds(normalProducts.map((p) => p.uuid));
        return;
      }
      // Handle selection - remove others and add selected
      const otherDiabetes = normalProducts.filter((p) => p.uuid !== productId);
      removeProductIds(otherDiabetes.map((p) => p.uuid));
      addProductIds(productId);
    },
    [normalProducts]
  );

  const updateBloodPressureMonitor = useCallback(
    (addBpm: boolean) => {
      const bpm = products.find((p) => p.name === BLOOD_PRESSURE_MONITOR_NAME);
      if (!bpm) return;
      if (addBpm) addProductIds(bpm.uuid);
      else removeProductIds(bpm.uuid);
    },
    [products]
  );

  const hasBloodPressureMonitor = useMemo(() => {
    const bpm = products.find((p) => p.name === BLOOD_PRESSURE_MONITOR_NAME);
    if (!bpm) return false;
    return selectedProductIds.includes(bpm.uuid);
  }, [products, selectedProductIds]);

  const numProducts = useMemo(() => {
    return products.filter((p) => selectedProductIds.includes(p.uuid)).length;
  }, [products, selectedProductIds]);

  const navigate = useNavigate();
  const { nextStep: nextPath } = useProductSelectionStep();
  const skipSelection = () => {
    removeProductIds(products.map((p) => p.uuid));
    // Auto-save session (non-blocking)
    saveSession();

    logger.action(LOGGER_ACTIONS.INTAKE.PRODUCT_SELECTION_SKIPPED, {
      product_category: CATEGORY_TYPE.DIABETES,
    });
    navigate({ to: nextPath });
  };

  const handleAddToOrder = () => {
    // Auto-save session (non-blocking)
    saveSession();
    navigate({ to: nextPath });
  };

  const noEligibleProducts = isSuccess && products.length === 0;
  if (noEligibleProducts) {
    return <Navigate to={nextPath} replace />;
  }

  const bpmExists = products.some((p) => p.name === 'Blood Pressure Monitor');

  return (
    <section>
      <ProductStepper steps={PRODUCT_STEPS} />
      <div className="space-y-6">
        <Card spacing="sm">
          <CardHeader>
            <CardTitle>Choose a diabetes glucose monitor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <ProductRadioGroup
              products={normalProducts}
              value={diabetesId}
              onValueChange={updateDiabetes}
            />

            {bpmExists && (
              <>
                <Hr />
                <div className="flex items-center gap-4">
                  <img
                    className="h-[78px] object-contain"
                    src={bloodPressureMonitorImage}
                    alt="Blood pressure monitor"
                  />
                  <div className="my-2 grid grid-rows-2 items-center justify-center gap-2">
                    <h3 className="font-circular">Add Blood Pressure Monitor</h3>
                    <RadioGroup
                      defaultValue={hasBloodPressureMonitor ? 'yes' : undefined}
                      onValueChange={(value) => updateBloodPressureMonitor(value === 'yes')}
                      className="flex gap-4"
                    >
                      <Label className="flex items-center gap-2 font-medium">
                        <RadioGroupItem id="bpm-yes" value="yes" />
                        Yes
                      </Label>
                      <Label className="flex items-center gap-2 font-medium">
                        <RadioGroupItem id="bpm-no" value="no" />
                        No
                      </Label>
                    </RadioGroup>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-[1fr_3fr] gap-3">
          <SkipButton categoryType={CATEGORY_TYPE.DIABETES} onSkip={skipSelection} />
          <Button onClick={handleAddToOrder}>Add to order ({numProducts})</Button>
        </div>
        <ProductSelectionSubmitFeedback currentCategory={CATEGORY_TYPE.DIABETES} />
      </div>
    </section>
  );
}
